import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import {
  DollarSign, ShoppingBag, Users, TrendingUp,
  Package, ArrowRight, Check, Circle, Globe,
  Rocket, Edit3, Sparkles, Lock, AlertTriangle, X, PartyPopper, CreditCard, Wand2,
} from 'lucide-react';
import {
  GlassCard, StatCardV2, DashboardBackdrop, GradientAreaChart,
} from '@/components/dashboard/dashboard-ui';
import AkiCopywriter from '@/components/ai/AkiCopywriter';
import IndustryQuizModal from './onboarding/IndustryQuizModal';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// ── Critical setup banner — appears until payments are connected AND site is published ──
const CriticalSetupBanner = ({ stripeConnected, sitePublished, onDismiss, dismissed }) => {
  if (dismissed) return null;
  if (stripeConnected && sitePublished) return null;
  const missing = [];
  if (!stripeConnected) missing.push({ label: 'Connect Stripe to accept payments', to: '/website-builder/settings/payments', icon: CreditCard });
  if (!sitePublished) missing.push({ label: 'Publish your site to go live', to: '/website-builder/settings/site-availability', icon: Rocket });
  return (
    <div
      data-testid="critical-setup-banner"
      className="relative overflow-hidden rounded-2xl border border-amber-400/25 bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-red-500/10 backdrop-blur-xl p-4 sm:p-5"
    >
      <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-amber-500/20 blur-3xl pointer-events-none" />
      <div className="relative flex items-start gap-4">
        <div className="w-10 h-10 rounded-xl bg-amber-500/25 border border-amber-400/40 flex items-center justify-center shrink-0">
          <AlertTriangle size={18} className="text-amber-300" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10.5px] uppercase tracking-[0.2em] text-amber-300 font-bold">Finish setup to start selling</p>
          <p className="text-white font-bold text-base mt-1">Your store isn&apos;t ready to accept orders yet</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {missing.map((m) => (
              <Link
                key={m.to}
                to={m.to}
                className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg bg-white/10 hover:bg-white/15 border border-white/15 text-white text-xs font-bold transition-colors"
                data-testid={`critical-setup-${m.to.split('/').pop()}`}
              >
                <m.icon size={12} /> {m.label} <ArrowRight size={12} />
              </Link>
            ))}
          </div>
        </div>
        <button
          onClick={onDismiss}
          className="p-1.5 rounded-lg text-white/50 hover:text-white hover:bg-white/5 transition-colors shrink-0"
          data-testid="critical-setup-dismiss"
          title="Dismiss until next login"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
};

// ── Celebration banner — when all setup tasks are complete ──
const CelebrationBanner = ({ onDismiss, dismissed }) => {
  if (dismissed) return null;
  return (
    <div
      data-testid="setup-celebration-banner"
      className="relative overflow-hidden rounded-2xl border border-emerald-400/25 bg-gradient-to-r from-emerald-500/15 via-cyan-500/15 to-violet-500/15 backdrop-blur-xl p-5"
    >
      <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-emerald-500/25 blur-3xl pointer-events-none" />
      <div className="relative flex items-center gap-4 flex-wrap">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-400 to-cyan-500 flex items-center justify-center shrink-0 shadow-[0_8px_24px_rgba(16,185,129,0.35)]">
          <PartyPopper size={22} className="text-white" />
        </div>
        <div className="flex-1 min-w-[200px]">
          <p className="text-[10.5px] uppercase tracking-[0.2em] text-emerald-300 font-bold">Setup Complete</p>
          <p className="text-white font-bold text-lg mt-0.5">You&apos;re ready to sell. Congrats!</p>
          <p className="text-white/70 text-sm mt-0.5">Time to share your site — invite your first customers.</p>
        </div>
        <button
          onClick={onDismiss}
          className="p-1.5 rounded-lg text-white/50 hover:text-white hover:bg-white/5 shrink-0"
          data-testid="celebration-dismiss"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
};

// ── Squarespace-style collapsible task group ──
const SetupGroup = ({ title, done, total, tasks, testid }) => {
  const [open, setOpen] = useState(true);
  return (
    <div data-testid={testid}>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 hover:bg-white/[0.03] transition-colors"
      >
        <div className="flex items-center gap-2.5">
          <ArrowRight size={14} className={`text-[#9CA3AF] transition-transform ${open ? 'rotate-90' : ''}`} />
          <p className="text-white font-bold">{title}</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-[#9CA3AF] font-mono">{done} / {total}</span>
          <div className="w-20 h-1.5 bg-white/10 rounded-full overflow-hidden">
            <div className="h-full rounded-full" style={{ width: total > 0 ? `${(done / total) * 100}%` : '0%', background: 'linear-gradient(90deg, #8B5CF6, #EC4899)' }} />
          </div>
        </div>
      </button>
      {open && (
        <div className="border-t border-white/5">
          {tasks.map((t, i) => (
            <Link
              key={t.id}
              to={t.link}
              className={`flex items-center gap-3 px-5 py-3.5 transition-colors hover:bg-white/[0.04] ${i !== tasks.length - 1 ? 'border-b border-white/5' : ''}`}
              data-testid={`setup-task-${t.id}`}
            >
              {t.done
                ? <span className="w-5 h-5 rounded-full flex items-center justify-center shrink-0" style={{ background: 'linear-gradient(135deg, #8B5CF6, #EC4899)', boxShadow: '0 4px 12px -2px rgba(139,92,246,0.55)' }}><Check size={11} className="text-white" strokeWidth={3} /></span>
                : <Circle size={20} className="text-[#9CA3AF] shrink-0" strokeWidth={1.4} />
              }
              <span className={`text-sm flex-1 ${t.done ? 'text-[#9CA3AF] line-through' : 'text-white'}`}>{t.label}</span>
              <ArrowRight size={14} className="text-[#9CA3AF] opacity-0 group-hover:opacity-100 transition-opacity" />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default function DashboardHome() {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [finances, setFinances] = useState(null);
  const [builderCompletion, setBuilderCompletion] = useState(null);
  const [site, setSite] = useState(null);
  const [settings, setSettings] = useState(null);
  const [previewImg, setPreviewImg] = useState('');
  const [vendorReady, setVendorReady] = useState(null);
  const [stripeConnected, setStripeConnected] = useState(false);
  const [criticalDismissed, setCriticalDismissed] = useState(
    typeof window !== 'undefined' && sessionStorage.getItem('wb:critical_banner_dismissed') === '1'
  );
  const [celebrationDismissed, setCelebrationDismissed] = useState(
    typeof window !== 'undefined' && localStorage.getItem('wb:celebration_dismissed') === '1'
  );
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/analytics`, { headers }),
      axios.get(`${API}/orders`, { headers }),
      axios.get(`${API}/products`, { headers }),
      axios.get(`${API}/finances/overview`, { headers }).catch(() => ({ data: null })),
      axios.get(`${API}/builder/my-site`, { headers }).catch(() => ({ data: null })),
      axios.get(`${API}/builder/settings`, { headers }).catch(() => ({ data: null })),
      axios.get(`${API}/builder/content`, { headers }).catch(() => ({ data: null })),
      axios.get(`${API}/stripe-connect/status`, { headers }).catch(() => ({ data: null })),
    ]).then(([analyticsRes, ordersRes, productsRes, financesRes, siteRes, settingsRes, contentRes, stripeRes]) => {
      setAnalytics(analyticsRes.data);
      setOrders(ordersRes.data?.slice(0, 5) || []);
      setProducts(productsRes.data?.slice(0, 4) || []);
      setFinances(financesRes.data);
      setSite(siteRes?.data || null);
      setSettings(settingsRes?.data || null);
      setStripeConnected(!!(stripeRes?.data?.connected));
      // Try to grab the first hero image from the content blocks for the preview thumbnail
      const blocks = contentRes?.data?.blocks || [];
      const hero = blocks.find(b => b.type === 'hero' || b.type === 'banner' || b.type === 'single_image');
      const heroImg = hero?.content?.image_url || hero?.content?.video_url || '';
      setPreviewImg(heroImg);
    });
    // Fetch builder completion
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/completion/builder`, { headers })
      .then(r => r.ok ? r.json() : null).then(d => { if (d) setBuilderCompletion(d); }).catch(() => {});
    // Fetch vendor-readiness (gates the Publish button)
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/completion/vendor`, { headers })
      .then(r => r.ok ? r.json() : null).then(d => { if (d) setVendorReady(d); }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismissCritical = () => {
    setCriticalDismissed(true);
    sessionStorage.setItem('wb:critical_banner_dismissed', '1');
  };
  const dismissCelebration = () => {
    setCelebrationDismissed(true);
    localStorage.setItem('wb:celebration_dismissed', '1');
  };

  const [akiOpen, setAkiOpen] = useState(false);
  const [quizOpen, setQuizOpen] = useState(false);

  // Auto-open industry quiz on first visit (no template yet, no seen flag)
  useEffect(() => {
    const seen = typeof window !== 'undefined' && localStorage.getItem('wb:onboarding-quiz-seen') === '1';
    const hasProducts = (products?.length || 0) > 0;
    if (!seen && !hasProducts && !site?.published) {
      const t = setTimeout(() => setQuizOpen(true), 1500);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, site]);

  const totalRevenue = analytics?.total_revenue || 0;
  const totalOrders = analytics?.total_orders || 0;
  const totalCustomers = analytics?.total_customers || 0;
  const conversionRate = analytics?.conversion_rate || 0;

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const salesData = analytics?.daily_sales?.map(d => ({
    name: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    revenue: d.revenue || 0,
    orders: (d.count || 0) * 10,
  })) || [
    { name: 'Week 1', revenue: 120, orders: 80 },
    { name: 'Week 2', revenue: 280, orders: 150 },
    { name: 'Week 3', revenue: 200, orders: 120 },
    { name: 'Week 4', revenue: 350, orders: 250 },
  ];

  // ── Sparkline series for each stat card ──
  const buildSpark = (key, fallback) => {
    const src = analytics?.daily_sales;
    if (Array.isArray(src) && src.length >= 4) {
      return src.slice(-8).map((d, i) => ({ x: i, y: Number(d[key] || 0) }));
    }
    return fallback.map((y, x) => ({ x, y }));
  };
  const revenueSpark   = buildSpark('revenue', [4, 9, 6, 12, 8, 15, 13, 18]);
  const ordersSpark    = buildSpark('count',   [2, 4, 3, 6, 5, 8, 7, 10]);
  const customersSpark = buildSpark('count',   [1, 2, 2, 4, 3, 5, 4, 7]);
  const aovSpark       = [3, 5, 4, 7, 6, 9, 8, 11].map((y, x) => ({ x, y }));

  const statusColors = {
    pending: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/20',
    completed: 'bg-green-500/15 text-[#86EFAC] border-green-500/20',
    shipped: 'bg-blue-500/15 text-[#93C5FD] border-blue-500/20',
    cancelled: 'bg-red-500/15 text-[#FCA5A5] border-red-500/20',
  };

  // ── Squarespace-style "Get started" checklist groups ──
  const setupTasks = [
    { id: 'header_logo',   label: 'Personalize your site header and logo', done: !!settings?.logo_url || !!settings?.brand_name, link: '/website-builder/settings' },
    { id: 'brand_style',   label: 'Customize your brand style',            done: !!settings?.accent_color,                       link: '/website-builder/styles' },
    { id: 'navigation',    label: 'Manage site navigation',                done: !!site?.site_header,                             link: '/website-builder/content' },
    { id: 'edit_content',  label: 'Edit site content',                     done: !!previewImg,                                   link: '/website-builder/content' },
    { id: 'products',      label: 'Add your products',                     done: (products?.length || 0) > 0,                    link: '/website-builder/products' },
    { id: 'payments',      label: 'Connect payments (Stripe)',             done: !!stripeConnected,                              link: '/website-builder/settings/payments' },
    { id: 'domain',        label: 'Set up a domain',                       done: !!site?.subdomain || !!site?.custom_domain,     link: '/website-builder/domains' },
    { id: 'publish',       label: 'Publish your site',                     done: !!site?.published,                              link: '/website-builder/settings/site-availability' },
  ];
  const setupDone = setupTasks.filter(t => t.done).length;
  const setupAllDone = setupDone === setupTasks.length;
  const invoicingTasks = [
    { id: 'create_invoice',   label: 'Create first invoice',     done: false, link: '/website-builder/finance' },
    { id: 'recurring',        label: 'Set up a recurring invoice', done: false, link: '/website-builder/finance' },
    { id: 'contact_form',     label: 'Add a contact form',       done: false, link: '/website-builder/content' },
    { id: 'connect_payments', label: 'Connect payment provider', done: !!stripeConnected, link: '/website-builder/settings/payments' },
  ];
  const invoicingDone = invoicingTasks.filter(t => t.done).length;

  const siteName = settings?.brand_name || site?.name || 'Your Site Title';
  const siteUrl = site?.subdomain
    ? `${site.subdomain}.entreprenatives.com`
    : (site?.custom_domain || 'yourname.entreprenatives.com');
  const isPrivate = !site?.published;
  const firstName = user?.first_name || user?.full_name?.split(' ')?.[0] || user?.email?.split('@')[0] || 'there';

  return (
    <div className="relative" data-testid="dashboard-home">
      <DashboardBackdrop />
      <div className="relative z-10 space-y-8">
      {/* Header — greeting + CUSTOMIZE / EDIT SITE corner buttons */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-[#A78BFA] font-bold mb-2" data-testid="welcome-eyebrow">
            {greeting()}
          </p>
          <h1 className="text-3xl sm:text-4xl font-bold text-white font-syne tracking-tight" data-testid="welcome-greeting">
            Welcome back, {firstName}
          </h1>
          <p className="text-sm text-[#A5A8C9] mt-1.5">Track your sales, complete setup, and edit your site — all in one place.</p>
        </div>
        <div className="flex items-center gap-2.5">
          <button
            onClick={() => setAkiOpen(true)}
            className="px-4 py-2.5 rounded-xl border border-[#A78BFA]/40 bg-[#A78BFA]/10 hover:bg-[#A78BFA]/15 text-white text-xs font-bold uppercase tracking-[0.16em] transition-all inline-flex items-center gap-2"
            data-testid="dashboard-aki-btn"
            title="Write with Aki — AI copywriter"
          >
            <Wand2 size={13} /> Ask Aki
          </button>
          <Link
            to="/website-builder/settings"
            className="px-4 py-2.5 rounded-xl border border-white/15 bg-[#13153A]/60 backdrop-blur text-white text-xs font-bold uppercase tracking-[0.16em] hover:bg-white/5 transition-all"
            data-testid="dashboard-customize-btn"
          >
            Customize Home
          </Link>
          <Link
            to="/website-builder/content"
            className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white text-xs font-bold uppercase tracking-[0.16em] shadow-[0_8px_24px_rgba(139,92,246,0.35)] hover:shadow-[0_8px_30px_rgba(139,92,246,0.55)] transition-all inline-flex items-center gap-2"
            data-testid="dashboard-edit-site-btn"
          >
            <Edit3 size={13} /> Edit Website
          </Link>
        </div>
      </div>

      {/* Iter 198j — Prominent EDIT WEBSITE hero band. Vendors kept asking
          "where's the edit button?" because the top-right corner CTA was
          easy to miss behind the designer-promo modal. This 100% full-width
          band on the dashboard home is impossible to miss. */}
      <Link
        to="/website-builder/content"
        className="block group relative overflow-hidden rounded-2xl border border-[#8B5CF6]/40 bg-gradient-to-r from-[#1a1231] via-[#211a3f] to-[#1a1231] hover:from-[#1f1637] hover:via-[#281e4a] hover:to-[#1f1637] transition-all"
        data-testid="dashboard-edit-website-hero"
      >
        <div className="relative flex items-center justify-between gap-6 px-6 py-5">
          <div className="flex items-center gap-4 min-w-0">
            <div className="shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-[#8B5CF6] to-[#6D28D9] flex items-center justify-center shadow-[0_8px_24px_rgba(139,92,246,0.4)]">
              <Edit3 size={20} className="text-white" />
            </div>
            <div className="min-w-0">
              <div className="text-white text-lg sm:text-xl font-bold font-syne tracking-tight">Edit your website</div>
              <div className="text-[#A5A8C9] text-xs sm:text-sm mt-0.5 truncate">Drag, drop, and design your live site — hero, sections, pages, and store.</div>
            </div>
          </div>
          <div className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white text-xs font-bold uppercase tracking-[0.16em] shadow-[0_6px_18px_rgba(139,92,246,0.4)] group-hover:shadow-[0_8px_24px_rgba(139,92,246,0.55)]">
            Open Editor
            <ArrowRight size={13} className="transition-transform group-hover:translate-x-0.5" />
          </div>
        </div>
        {/* subtle spotlight */}
        <div className="pointer-events-none absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[#8B5CF6]/10 blur-3xl" />
      </Link>

      {/* Critical setup / celebration banners */}
      <CriticalSetupBanner
        stripeConnected={stripeConnected}
        sitePublished={!!site?.published}
        onDismiss={dismissCritical}
        dismissed={criticalDismissed}
      />
      {setupAllDone && (
        <CelebrationBanner onDismiss={dismissCelebration} dismissed={celebrationDismissed} />
      )}

      {/* GET STARTED — Squarespace-style checklist + site preview side panel */}
      <div className="grid lg:grid-cols-3 gap-6" data-testid="get-started-panel">
        {/* Left — checklist column (spans 2) */}
        <div className="lg:col-span-2 space-y-4">
          <h2 className="text-xl font-bold text-white">Get started</h2>

          {/* Set up your website group */}
          <GlassCard className="overflow-hidden">
            <SetupGroup
              title="Set up your website"
              done={setupDone}
              total={setupTasks.length}
              tasks={setupTasks}
              testid="setup-website-group"
            />
          </GlassCard>

          {/* Set up invoicing group */}
          <GlassCard className="overflow-hidden">
            <SetupGroup
              title="Set up invoicing"
              done={invoicingDone}
              total={invoicingTasks.length}
              tasks={invoicingTasks}
              testid="setup-invoicing-group"
            />
          </GlassCard>
        </div>

        {/* Right — Live preview panel */}
        <GlassCard className="overflow-hidden self-start" data-testid="site-preview-panel">
          <div className="p-4 pb-3">
            <div className="relative aspect-[4/3] bg-gradient-to-br from-[#1E1E3A] to-[#0E0E23] rounded-xl overflow-hidden border border-white/10">
              {previewImg ? (
                <img
                  src={previewImg.startsWith('http') ? previewImg : `${process.env.REACT_APP_BACKEND_URL}${previewImg}`}
                  alt="Site preview"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-[#9CA3AF] text-xs">
                  <div className="text-center">
                    <Sparkles size={20} className="mx-auto opacity-50 mb-2" />
                    <p>Preview your site here</p>
                  </div>
                </div>
              )}
              {/* tiny site-title chip top-left to mimic Squarespace */}
              <div className="absolute top-2 left-2 right-2 flex items-center justify-between text-[10px] font-bold text-white drop-shadow">
                <span className="uppercase tracking-wider">{siteName}</span>
                <div className="hidden sm:flex gap-2 opacity-70 text-[9px]">
                  <span>Shop</span><span>Services</span><span>About</span><span>Contact</span>
                </div>
              </div>
              {isPrivate && (
                <div className="absolute bottom-2 right-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-black/55 backdrop-blur text-white text-[10px] font-semibold">
                  <Lock size={10} /> Private
                </div>
              )}
            </div>
          </div>
          <div className="px-5 pb-5">
            <p className="text-white font-bold text-base">{siteName}</p>
            <a
              href={`https://${siteUrl}`}
              target="_blank"
              rel="noreferrer"
              className="text-[#A78BFA] text-xs hover:underline break-all"
              data-testid="site-url"
            >
              {siteUrl}
            </a>
            <div className="mt-4 space-y-2">
              <Link
                to="/website-builder/domains"
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-white/15 bg-white/[0.03] hover:bg-white/[0.06] text-white text-xs font-bold uppercase tracking-[0.16em] transition-all"
                data-testid="get-custom-domain-btn"
              >
                <Globe size={13} /> Get a Custom Domain
              </Link>
              {vendorReady?.ready_to_sell === false ? (
                <Link
                  to="/dashboard/vendor/services"
                  className="w-full flex flex-col items-center justify-center gap-1 px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white/70 text-xs font-bold uppercase tracking-[0.16em]"
                  data-testid="publish-site-btn-locked"
                  title="Finish your profile before publishing"
                >
                  <span className="flex items-center gap-2"><Rocket size={13} /> Publish · Profile {vendorReady.percentage}%</span>
                  <span className="text-[10px] normal-case tracking-normal text-white/55">Finish setup to go live →</span>
                </Link>
              ) : (
                <Link
                  to="/website-builder/settings"
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white text-xs font-bold uppercase tracking-[0.16em] shadow-[0_8px_24px_rgba(139,92,246,0.35)] hover:shadow-[0_8px_30px_rgba(139,92,246,0.55)] transition-all"
                  data-testid="publish-site-btn"
                >
                  <Rocket size={13} /> Publish Your Site
                </Link>
              )}
            </div>
          </div>
        </GlassCard>
      </div>

      {/* Original builder setup progress bar (kept hidden — superseded by Get Started above) */}
      {false && builderCompletion && builderCompletion.percentage < 100 && (
        <div className="p-5 rounded-2xl bg-[#2D2D52]/60 backdrop-blur-xl border border-[#8B5CF6]/20" data-testid="builder-completion">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white font-bold flex items-center gap-2"><span>🔧</span> Website Setup</h3>
            <span className="text-[#A78BFA] font-mono font-bold text-lg">{builderCompletion.percentage}%</span>
          </div>
          <div className="w-full h-3 bg-white/5 rounded-full overflow-hidden mb-4">
            <div className="h-full bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] rounded-full transition-all" style={{ width: `${builderCompletion.percentage}%` }} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {builderCompletion.steps.map((step) => (
              <div key={step.id} className={`flex items-center gap-2 text-sm py-1.5 px-3 rounded-lg ${step.done ? 'text-green-400' : 'text-[#9CA3AF]'}`}>
                {step.done ? <span className="text-green-400">✓</span> : <span className="text-[#64748B]">○</span>}
                <span className={step.done ? 'line-through opacity-60' : ''}>{step.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Metric Cards — glass + gradient icon + count-up + sparkline */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCardV2
          icon={DollarSign}
          label="Total Sales"
          value={totalRevenue}
          prefix="$"
          change={12.5}
          positive
          ramp="violet"
          spark={revenueSpark}
          sparkId="sales"
          data-testid="metric-total-sales"
          delay={0}
        />
        <StatCardV2
          icon={ShoppingBag}
          label="Total Orders"
          value={totalOrders}
          change={8.2}
          positive
          ramp="fuchsia"
          spark={ordersSpark}
          sparkId="orders"
          data-testid="metric-total-orders"
          delay={0.08}
        />
        <StatCardV2
          icon={Users}
          label="Customers"
          value={totalCustomers}
          change={4.7}
          positive
          ramp="cyan"
          spark={customersSpark}
          sparkId="customers"
          data-testid="metric-customers"
          delay={0.16}
        />
        <StatCardV2
          icon={TrendingUp}
          label="Avg. Order Value"
          value={totalOrders > 0 ? totalRevenue / totalOrders : 0}
          prefix="$"
          decimals={0}
          change={3.1}
          positive
          ramp="amber"
          spark={aovSpark}
          sparkId="aov"
          data-testid="metric-average-order-value"
          delay={0.24}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sales Performance Chart */}
        <GlassCard glow className="lg:col-span-2 p-6" data-testid="sales-chart">
          <div className="flex items-center justify-between mb-5">
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-[#A78BFA] font-bold mb-1">Performance</p>
              <h3 className="text-lg font-bold text-white font-syne">Sales Over Time</h3>
              <p className="text-xs text-[#A5A8C9] mt-0.5">Revenue trend across recent periods</p>
            </div>
            <div className="hidden sm:flex items-center gap-4 text-[11px]">
              <span className="inline-flex items-center gap-1.5 text-[#A5A8C9]">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#8B5CF6', boxShadow: '0 0 10px rgba(139,92,246,0.7)' }} />
                Revenue
              </span>
              <span className="inline-flex items-center gap-1.5 text-[#A5A8C9]">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#22D3EE', boxShadow: '0 0 10px rgba(34,211,238,0.7)' }} />
                Orders
              </span>
            </div>
          </div>
          <GradientAreaChart
            data={salesData}
            height={280}
            series={[
              { key: 'revenue', label: 'Revenue', ramp: 'violet' },
              { key: 'orders',  label: 'Orders',  ramp: 'cyan'   },
            ]}
            yFormatter={(v) => `$${Number(v).toLocaleString()}`}
          />
        </GlassCard>

        {/* Top Selling Products */}
        <GlassCard className="overflow-hidden" data-testid="top-products">
          <div className="p-6 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-white">Top Selling Products</h3>
                <p className="text-xs text-[#9CA3AF] mt-1">The Most Popular Items</p>
              </div>
              <Link to="/dashboard/products" className="text-xs text-[#60A5FA] hover:text-[#93C5FD] transition-colors font-medium">
                See All
              </Link>
            </div>
          </div>
          <div className="px-6 pb-6 space-y-3">
            {products.map((product) => (
              <div key={product.id} className="flex items-center gap-4 p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors group" data-testid={`top-product-${product.id}`}>
                <div className="w-11 h-11 rounded-xl overflow-hidden bg-white/5 shrink-0">
                  {product.images?.[0] ? (
                    <img src={product.images[0].startsWith('/api') ? process.env.REACT_APP_BACKEND_URL + product.images[0] : product.images[0]} alt={product.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><Package size={16} className="text-[#9CA3AF]" /></div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-semibold truncate">{product.name}</p>
                  <p className="text-[#9CA3AF] text-xs">${product.price?.toFixed(2)}</p>
                </div>
              </div>
            ))}
            {products.length === 0 && (
              <div className="text-center py-6 text-[#9CA3AF]">
                <Package size={28} className="mx-auto mb-2 opacity-40" />
                <p className="text-sm">No products yet</p>
              </div>
            )}
          </div>
        </GlassCard>
      </div>

      {/* Recent Orders */}
      <GlassCard className="overflow-hidden" data-testid="recent-orders">
        <div className="flex items-center justify-between p-6 pb-0">
          <div>
            <h3 className="text-lg font-semibold text-white">Recent Orders</h3>
            <p className="text-xs text-[#9CA3AF]">Latest transactions</p>
          </div>
          <Link to="/dashboard/orders" className="text-xs text-[#60A5FA] hover:text-[#93C5FD] transition-colors font-medium" data-testid="view-all-orders">
            View All
          </Link>
        </div>
        <div className="p-6">
          <table className="w-full text-left text-sm" data-testid="orders-table">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-[#9CA3AF] border-b border-white/10">
                <th className="pb-3 font-medium">Customer</th>
                <th className="pb-3 font-medium">Total</th>
                <th className="pb-3 font-medium">Status</th>
                <th className="pb-3 font-medium">Date</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <tr key={order.id} className="border-b border-white/5 hover:bg-white/5 transition-colors" data-testid={`order-row-${order.id}`}>
                  <td className="py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#8B5CF6]/40 to-[#3B82F6]/40 flex items-center justify-center text-xs font-bold text-white">
                        {order.customer_name?.[0]?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <p className="text-white text-sm">{order.customer_name}</p>
                        <p className="text-[#9CA3AF] text-xs">{order.customer_email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-4 text-white font-semibold">${order.total?.toFixed(2)}</td>
                  <td className="py-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${statusColors[order.status] || statusColors.pending}`}>
                      {order.status}
                    </span>
                  </td>
                  <td className="py-4 text-[#9CA3AF] text-xs">
                    {order.created_at ? new Date(order.created_at).toLocaleDateString() : '-'}
                  </td>
                </tr>
              ))}
              {orders.length === 0 && (
                <tr><td colSpan={4} className="py-12 text-center text-[#9CA3AF]">No orders yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </GlassCard>
      <AkiCopywriter
        open={akiOpen}
        onClose={() => setAkiOpen(false)}
        defaultKind="hero_headline"
        brand={settings?.brand_name || ''}
        description={settings?.description || ''}
      />
      <IndustryQuizModal
        open={quizOpen}
        onClose={() => setQuizOpen(false)}
        onTemplateSelect={(tpl) => {
          // Store the picked template id for the templates gallery to highlight
          try { localStorage.setItem('wb:onboarding-template-pick', tpl.id); } catch {}
          window.location.href = '/website-builder/templates';
        }}
      />
      </div>
    </div>
  );
}
